import { WarehouseItemWorkRequestKind } from "@redotech/crdb-enums/fulfillment/warehouse-item-work-request-kind";
import { Translate, useTranslation } from "@redotech/redo-i18n";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import type { MakeServiceRequestState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { WORK_REQUEST_KINDS } from "@redotech/redo-warehouse-common/item-work-request-kind";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

export const MakeServiceRequestDetails = memo(
  function MakeServiceRequestDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<MakeServiceRequestState>) {
    const { t } = useTranslation();
    const { data: instructionsData, isLoading } = useRpcQuery(
      RedoOutboundLabelsRpcClientContext,
      { method: "listPhotoInstructions" as const, args: {} },
    );

    const kindOptions = useMemo(
      () =>
        WORK_REQUEST_KINDS.map((kind) => ({
          id: kind,
          type: "text" as const,
          text: getWorkRequestKindLabel(t, kind),
          value: kind,
        })),
      [t],
    );

    const guidanceOptions = useMemo(
      () =>
        (instructionsData?.instructions ?? []).map((instructions) => ({
          id: instructions.id,
          type: "text" as const,
          text: instructions.description,
          value: instructions.id,
        })),
      [instructionsData],
    );

    return (
      <Flex dir="column" gap="md">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Request type")}
          </Text>
          <RedoSingleSelectDropdownInput<WarehouseItemWorkRequestKind>
            options={kindOptions}
            optionSelected={({ item }) =>
              setState((state) => ({ ...state, requestKind: item.value }))
            }
            placeholder={t("Select request type")}
            selectedItem={kindOptions.find(
              (option) => option.value === state.requestKind,
            )}
            size="xs"
          />
        </Flex>
        {state.requestKind === WarehouseItemWorkRequestKind.Photo && (
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Photo request guidance")}
            </Text>
            <RedoSingleSelectDropdownInput<string>
              emptyListMessage={t("No photo guidance configured yet")}
              itemsLoading={isLoading}
              options={guidanceOptions}
              optionSelected={({ item }) =>
                setState((state) => ({
                  ...state,
                  photoInstructionsId: item.value,
                }))
              }
              placeholder={t("Select photo guidance")}
              selectedItem={guidanceOptions.find(
                (option) => option.value === state.photoInstructionsId,
              )}
              size="xs"
            />
            <Text fontSize="xs" textColor="secondary">
              {t(
                "The warehouse walks these steps when it picks the request up.",
              )}
            </Text>
          </Flex>
        )}
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Next step")}
          </Text>
          <Text fontSize="xs" textColor="secondary">
            {t(
              "The request is queued in the background; the worker is not interrupted and moves straight to the next step.",
            )}
          </Text>
          {stepSelect({
            value: state.next,
            valueChange: (next) => setState((state) => ({ ...state, next })),
            handleIndex: 0,
          })}
        </Flex>
      </Flex>
    );
  },
);

function getWorkRequestKindLabel(
  t: Translate,
  kind: WarehouseItemWorkRequestKind,
): string {
  switch (kind) {
    case WarehouseItemWorkRequestKind.Photo:
      return t("Photo request");
    case WarehouseItemWorkRequestKind.Qa:
      return t("QA request", { context: "quality assurance" });
  }
}
